import { z } from "zod";

import { moneyText } from "../money";

/**
 * The subscription: the plan, where the pharmacy stands, and — for owners —
 * the prices and how to pay. `GET /billing`.
 *
 * The phone shows only where the pharmacy stands (Google Play's rules: no
 * prices and no way to pay inside the Play app). The web version's Billing
 * page shows everything. Prices, networks and numbers live in ONE place on
 * the server (apps/api/src/modules/billing/billing.config.ts), so neither app
 * contains them.
 */

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Worked out from "paid until" and today: see planStatus in @pharmacy/domain. */
export const planStatusResponse = z.object({
  state: z.enum(["free", "active", "ending", "grace", "readOnly"]),
  /** The last day paid for (or free until); null: free without end. */
  paidThrough: isoDay.nullable(),
  /** 0 on the last day; negative after it; null when free. */
  daysLeft: z.number().int().nullable(),
  /** The last day of grace. */
  graceUntil: isoDay.nullable(),
  /** The first read-only day. */
  readOnlyFrom: isoDay.nullable(),
});

export const planPriceResponse = z.object({
  plan: z.enum(["basic", "pro"]),
  months: z.number().int(),
  /** For the whole period. */
  amount: moneyText,
  /** The same, per month: shows the saving on 12 months. */
  perMonth: moneyText,
});

export const payNetworkResponse = z.object({
  /** evcplus · edahab */
  id: z.string(),
  /** "EVC Plus" */
  name: z.string(),
  /** The number that receives the money, as it is dialled: 617269566 */
  number: z.string(),
  /** The code to dial, with `{amount}` to fill in: "*712*617269566*{amount}#" */
  ussd: z.string(),
});

export const billingResponse = z.object({
  /**
   * Whether plans are being charged yet. Until the owner starts paid plans,
   * nothing is refused and the apps show no notices.
   */
  enforced: z.boolean(),
  plan: z.enum(["basic", "pro"]),
  status: planStatusResponse,
  /**
   * The days up to "paid until" were paid for, rather than given free (the
   * trial, the pilot's 30 days, or free days added by hand): "Paid until"
   * rather than "Free until".
   */
  paid: z.boolean(),
  limits: z.object({ branches: z.number().int(), staff: z.number().int().nullable() }),
  usage: z.object({ branches: z.number().int(), staff: z.number().int() }),
  /** Owners only: every plan and period with its price. */
  prices: z.array(planPriceResponse).optional(),
  /** Owners only: how to pay. */
  networks: z.array(payNetworkResponse).optional(),
  /** Owners only: where to send the pharmacy's name after paying (E.164). */
  whatsApp: z.string().optional(),
});

export type PlanStatusResponse = z.infer<typeof planStatusResponse>;
export type PlanPriceResponse = z.infer<typeof planPriceResponse>;
export type PayNetworkResponse = z.infer<typeof payNetworkResponse>;
export type BillingResponse = z.infer<typeof billingResponse>;
